import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useLanguage } from '../context/LanguageContext';

const AhilyadeviForm = () => {
  const formRef = useRef();
  const { language } = useLanguage();

  const handleDownloadPdf = async () => {
    try {
      const element = formRef.current;
      const opt = {
        margin: 0,
        filename: 'Ahilyadevi_Puraskar_Form.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      // We handle page breaks manually using A4 aspect ratio containers
      const pages = element.querySelectorAll('.pdf-page');
      const pdf = new jsPDF(opt.jsPDF);

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const canvas = await html2canvas(page, opt.html2canvas);
        const imgData = canvas.toDataURL('image/jpeg', 1.0);

        if (i > 0) pdf.addPage();

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      pdf.save(opt.filename);
    } catch (e) {
      console.error('PDF Generation Failed', e);
      window.print(); // Fallback
    }
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans">
      {/* Floating Action Buttons (Hidden when printing/generating PDF) */}
      <div className="fixed top-4 right-4 flex space-x-4 z-50 print-hide">
        <button
          onClick={() => window.print()}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow-lg flex items-center gap-2"
        >
          <Printer size={18} /> {language === 'marathi' ? 'प्रिंट करा' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow-lg flex items-center gap-2"
        >
          <Download size={18} /> {language === 'marathi' ? 'पीडीएफ डाउनलोड करा' : 'Download PDF'}
        </button>
      </div>

      <style>{`
        @media print {
          body { background: white; }
          .print-hide { display: none !important; }
          .pdf-page {
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: always;
          }
        }
        .dotted-line {
          border-bottom: 2px dotted #000;
          display: inline-block;
          min-width: 200px;
        }
      `}</style>

      <div ref={formRef} className="flex flex-col items-center gap-8">

        {/* PAGE 1 - Title Page */}
        <div className="pdf-page bg-white w-[210mm] h-[297mm] shadow-xl relative overflow-hidden shrink-0 flex flex-col items-center p-12">
          {/* Decorative left border line */}
          <div className="absolute left-10 top-0 bottom-0 w-3 bg-[#b94a48]"></div>

          <div className="mt-16 w-48 h-56 bg-gray-100 rounded-lg border-4 border-gray-200 overflow-hidden shadow-md flex items-center justify-center">
            <span className="text-gray-400 text-sm font-bold text-center">
              {language === 'marathi' ? 'अहिल्यादेवी होळकर\n(इथे फोटो टाका)' : 'Ahilyadevi Holkar\n(Place Photo Here)'}
            </span>
          </div>

          <div className="w-full flex justify-start mt-8 relative left-[-20px]">
            <div className="bg-[#b94a48] text-white px-10 py-3 text-2xl font-bold rounded-r-full shadow-md ml-10">
              {language === 'marathi' ? 'सन २०२५' : 'Year 2025'}
            </div>
          </div>

          <div className="mt-16 text-center z-10 font-bold text-[#0066cc]">
            <h1 className="text-5xl leading-tight mb-2 tracking-wide font-serif">
              {language === 'marathi' ? '“पुण्यश्लोक अहिल्यादेवी होळकर' : '“Punyashlok Ahilyadevi Holkar'}
            </h1>
            <h2 className="text-5xl leading-tight tracking-wide font-serif">
              {language === 'marathi' ? 'महिला सन्मान पुरस्कार- २०२५ ”' : 'Women\'s Honor Award - 2025 ”'}
            </h2>
          </div>

          <div className="mt-20 w-[80%] border-2 border-[#b94a48] rounded-[30px] p-10 bg-white z-10 shadow-sm relative">
            <div className="space-y-8 text-xl font-bold">
              <div className="flex">
                <span className="w-40">{language === 'marathi' ? 'ग्रामपंचायत:' : 'Gram Panchayat:'}</span>
                <span className="flex-1 border-b-2 border-gray-400 border-dashed"></span>
              </div>
              <div className="flex">
                <span className="w-40">{language === 'marathi' ? 'पंचायत समिती:' : 'Panchayat Samiti:'}</span>
                <span className="flex-1 border-b-2 border-gray-400 border-dashed"></span>
              </div>
              <div className="flex">
                <span className="w-40">{language === 'marathi' ? 'जिल्हा परिषद:' : 'Zilla Parishad:'}</span>
                <span className="flex-1 border-b-2 border-gray-400 border-dashed"></span>
              </div>
            </div>
          </div>

          <div className="absolute bottom-10 text-blue-500 border border-blue-100 px-4 py-2 bg-white font-medium text-lg">
            www.majhigrampanchayat.com
          </div>
        </div>

        {/* PAGE 2 - Notice */}
        <div className="pdf-page bg-white w-[210mm] min-h-[297mm] shadow-xl relative shrink-0 px-20 py-16 text-black text-lg">
          <div className="font-bold text-sm mb-8 text-justify leading-relaxed">
            {language === 'marathi' 
              ? 'वाचा: महिला व बालकल्याण विभाग, महाराष्ट्र शासन, शासन निर्णय दि. ९ मे २०२३ व शासन शुद्धिपत्रक दि. १५ मे २०२३'
              : 'Read: Women and Child Welfare Department, Govt of Maharashtra, GR dated May 9, 2023 & Corrigendum dated May 15, 2023'}
          </div>

          <div className="flex justify-end mb-8 font-semibold text-base leading-loose">
            <div className="w-80">
              <div>{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'} <span className="border-b border-black border-dashed inline-block w-40"></span></div>
              <div>{language === 'marathi' ? 'ता.' : 'Taluka: '}<span className="border-b border-black border-dashed inline-block w-28"></span> {language === 'marathi' ? 'जि.' : 'Dist.:'} <span className="border-b border-black border-dashed inline-block w-28"></span></div>
              <div>{language === 'marathi' ? 'दिनांक:' : 'Date:'} ----/०५/२०२५</div>
            </div>
          </div>

          <h3 className="text-center text-2xl font-bold underline underline-offset-8 mb-8 decoration-2">
            {language === 'marathi' ? 'प्रसिद्धी पत्रक/जाहीर सूचना' : 'Press Release / Public Notice'}
          </h3>

          <p className="text-justify leading-relaxed font-medium mb-6 indent-16">
            {language === 'marathi' 
              ? <>या प्रसिद्धीपत्रक/ जाहीर सूचनेद्वारे ग्रामपंचायत <span className="border-b border-black border-dashed inline-block w-56"></span> येथील सर्व महिला व ग्रामस्थांना कळविण्यात येते की, संदर्भीय शासन निर्णयानुसार पुण्यश्लोक अहिल्यादेवी होळकर यांच्या जयंती निमित्त दि. ३१ मे २०२५ रोजी ग्रामपंचायत/गट ग्रामपंचायत क्षेत्रातील सामाजिक तसेच महिला व बालकल्याण क्षेत्रात उत्कृष्ट कार्य करीत असलेल्या दोन कर्तबगार महिलांना <span className="font-bold">“पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार”</span> ग्रामपंचायतीमार्फत देण्यात येणार आहे.</>
              : <>Through this press release/public notice, all women and villagers of Gram Panchayat <span className="border-b border-black border-dashed inline-block w-56"></span> are informed that according to the reference government resolution, on the occasion of the birth anniversary of Punyashlok Ahilyadevi Holkar on May 31, 2025, the <span className="font-bold">"Punyashlok Ahilyadevi Holkar Women's Honor Award"</span> will be given by the Gram Panchayat to two capable women working excellently in the social as well as women and child welfare fields in the Gram Panchayat/Group Gram Panchayat area.</>}
          </p>

          <div className="mb-6 font-semibold">
            <div className="flex mb-2"><span className="w-6 font-bold">■</span><span className="font-bold text-xl">{language === 'marathi' ? 'पुरस्काराचे स्वरुप:' : 'Nature of Award:'}</span></div>
            <div className="grid grid-cols-2 gap-y-4 ml-6">
              <div>{language === 'marathi' ? '१) सन्मानपत्र' : '1) Certificate of Honor'}</div>
              <div>{language === 'marathi' ? '३) शाल व श्रीफळ (नारळ)' : '3) Shawl and Coconut'}</div>
              <div>{language === 'marathi' ? '२) सन्मानचिन्ह' : '2) Memento'}</div>
              <div>{language === 'marathi' ? '४) रोख रक्कम (रु.५०० प्रति महिला)' : '4) Cash Amount (Rs. 500 per woman)'}</div>
            </div>
          </div>

          <div className="font-semibold">
            <div className="flex mb-4"><span className="w-6 font-bold">■</span><span className="font-bold text-xl">{language === 'marathi' ? 'पात्रता निकष:' : 'Eligibility Criteria:'}</span></div>
            <ol className="list-decimal pl-10 space-y-2 text-[17px] leading-relaxed">
              {language === 'marathi' ? (
                <>
                  <li>सामाजिक क्षेत्रात, महिला व बाल विकास क्षेत्रामध्ये उत्कृष्ट/उल्लेखनीय कार्य करीत असलेल्या महिला,</li>
                  <li>सदर महिला ह्या त्याच ग्रामपंचायतीतील / गट ग्रामपंचायतीतील रहिवासी असावी,</li>
                  <li>त्याचे कार्य हे त्याच ग्रामपंचायती/गट ग्रामपंचायतीमध्ये केलेले असावे,</li>
                  <li>महिला व बाल विकास क्षेत्रात किमान ३ वर्ष कार्य केलेले असावे,</li>
                  <li>पुरस्कार प्राप्त महिला या सात वर्षांनंतर सदर पुरस्कारासाठी पुन्हा पात्र ठरतील,</li>
                  <li>महिलांच्या समस्या व प्रश्नांबाबत जाणीव व संवेदनशीलता असावी.</li>
                  <li>सामाजिक क्षेत्रात सक्रीय सहभाग असावा,</li>
                  <li>कोणत्याही प्रकारचा गुन्हा दाखल नसावा.</li>
                  <li>बाल विवाह प्रतिबंध, हुंडा निर्मुलन, लिंग चिकीत्सा प्रतिबंध, घरगुती हिंसा प्रतिबंध, महिला सक्षमीकरण, महिला स्वयंसहाय्यता बचत गट, आरोग्य, साक्षरता मुलींच्या शिक्षणासाठी पुढाकार या सारख्या कार्यामध्ये उत्स्फूर्तपणे पुढाकार घेतलेला असावा.</li>
                </>
              ) : (
                <>
                  <li>Women doing excellent/remarkable work in social field, women and child development field,</li>
                  <li>The woman should be a resident of the same Gram Panchayat / Group Gram Panchayat,</li>
                  <li>Her work should be done in the same Gram Panchayat / Group Gram Panchayat,</li>
                  <li>Must have worked in women and child development field for at least 3 years,</li>
                  <li>Award winning women will be eligible for the award again after seven years,</li>
                  <li>Should have awareness and sensitivity towards women's problems and issues.</li>
                  <li>Should have active participation in the social field,</li>
                  <li>No crime should be registered.</li>
                  <li>Should have spontaneously taken initiative in works like prevention of child marriage, eradication of dowry, prevention of sex determination, prevention of domestic violence, women empowerment, women self-help groups, health, literacy, initiative for girls' education.</li>
                </>
              )}
            </ol>
          </div>

          <p className="text-justify leading-relaxed mt-6 indent-16 font-medium">
            {language === 'marathi' 
              ? 'करिता इच्छुक महिलांनी स्वत: ची वैयक्तिक माहिती व केलेल्या कार्याच्या तपशीलासह आपला अर्ज दिनांक -----/०५/२०२५ पर्यंत ग्रामपंचायत कार्यालयात सादर करावे.'
              : 'Therefore, interested women should submit their application along with personal information and details of work done to the Gram Panchayat office by Date -----/05/2025.'}
          </p>

          <div className="flex justify-between mt-20 font-bold ml-12">
            <div>{language === 'marathi' ? 'सोबत: अर्जाचा नमुना' : 'Enclosure: Application Form Format'}</div>
            <div className="text-center mr-16">
              <div className="mb-8">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</div>
              <div>{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'} -------------------------</div>
            </div>
          </div>
        </div>

        {/* PAGE 3 - Application Form */}
        <div className="pdf-page bg-white w-[210mm] min-h-[297mm] shadow-xl relative shrink-0 px-20 py-16 text-black text-[17px]">
          <h3 className="text-center text-[22px] font-bold underline underline-offset-8 mb-10 decoration-2">
            {language === 'marathi' 
              ? 'पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कारासाठी अर्जाचा नमुना'
              : 'Application Form for Punyashlok Ahilyadevi Holkar Women\'s Honor Award'}
          </h3>

          <div className="flex justify-between mb-4 font-semibold">
            <div>{language === 'marathi' ? 'प्रति,' : 'To,'}</div>
            <div>{language === 'marathi' ? 'दिनांक:' : 'Date:'} /०५/२०२५</div>
          </div>

          <div className="font-semibold leading-relaxed mb-6">
            <div>{language === 'marathi' ? 'मा. सरपंच तथा पुरस्कार निवड समिती अध्यक्ष,' : 'Hon. Sarpanch and Chairman of Award Selection Committee,'}</div>
            <div>{language === 'marathi' ? 'पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार निवड समिती' : 'Punyashlok Ahilyadevi Holkar Women\'s Honor Award Selection Committee'}</div>
            <div>{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'} ----------------------------------------</div>
            <div>{language === 'marathi' ? 'ता.' : 'Taluka:'} ---------------------------------- {language === 'marathi' ? 'जि.' : 'Dist.:'}----------------------------------</div>
          </div>

          <div className="font-bold text-center leading-relaxed max-w-[85%] mx-auto mb-6">
            <div>{language === 'marathi' ? 'विषय : पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कारासाठी अर्ज सादर करणेबाबत.' : 'Subject: Regarding submission of application for Punyashlok Ahilyadevi Holkar Women\'s Honor Award.'}</div>
            <div className="mt-4">{language === 'marathi' ? 'संदर्भ: महिला व बालकल्याण विभाग, महाराष्ट्र शासन, शासन निर्णय दि. ९ मे २०२३ व शासन शुद्धिपत्रक दि. १५ मे २०२३' : 'Ref: Women and Child Welfare Dept, Govt of Maharashtra, GR dt May 9, 2023 & Corrigendum dt May 15, 2023'}</div>
          </div>

          <div className="font-medium leading-relaxed mb-8">
            <div>{language === 'marathi' ? 'मा. महोदय,' : 'Respected Sir/Madam,'}</div>
            <p className="indent-12 mt-4 text-justify">
              {language === 'marathi' 
                ? 'वरील संदर्भीय विषयान्वये ग्रामपंचायत ------------------------- येथील पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार- २०२५ साठी मी माझा अर्ज सादर करत असून माझी वैयक्तिक व सामाजिक कार्याची माहिती खालीलप्रमाणे आहे.'
                : 'With reference to the above subject, I am submitting my application for the Punyashlok Ahilyadevi Holkar Women\'s Honor Award-2025 at Gram Panchayat -------------------------, and my personal and social work information is as follows.'}
            </p>
          </div>

          <div className="font-bold text-xl mb-6">{language === 'marathi' ? 'अर्जदाराची वैयक्तिक माहिती :' : 'Applicant\'s Personal Information:'}</div>

          <div className="flex justify-between relative mb-8">
            <div className="space-y-5 font-semibold w-full pr-40">
              <div className="flex">
                <span className="w-8 ml-2">•</span><span className="w-36">{language === 'marathi' ? 'संपूर्ण नाव' : 'Full Name'}</span>
                <span className="mr-2">:</span>
                <span className="flex-1 border-b border-black border-dashed"></span>
              </div>
              <div className="flex items-start">
                <span className="w-8 ml-2">•</span><span className="w-36 pt-1">{language === 'marathi' ? 'रहिवासी पत्ता' : 'Residential Address'}</span>
                <span className="mr-2 pt-1">:</span>
                <div className="flex-1 flex flex-col space-y-6 mt-1">
                  <span className="border-b border-black border-dashed"></span>
                  <span className="border-b border-black border-dashed"></span>
                </div>
              </div>
              <div className="flex mt-8">
                <span className="w-8 ml-2">•</span><span className="w-36">{language === 'marathi' ? 'जन्मदिनांक/वय' : 'DOB / Age'}</span>
                <span className="mr-2">:</span>
                <span className="flex-1 border-b border-black border-dashed"></span>
              </div>
              <div className="flex">
                <span className="w-8 ml-2">•</span><span className="w-36">{language === 'marathi' ? 'दूरध्वनी / मोबाइल क्र.' : 'Phone / Mobile No.'}</span>
                <span className="mr-2">:</span>
                <span className="flex-1 border-b border-black border-dashed"></span>
              </div>
              <div className="flex">
                <span className="w-8 ml-2">•</span><span className="w-36">{language === 'marathi' ? 'शिक्षण:' : 'Education:'}</span>
                <span className="mr-2">:</span>
                <span className="flex-1 border-b border-black border-dashed"></span>
              </div>
            </div>

            {/* Photo Box */}
            <div className="absolute right-0 top-0 w-32 h-40 border-2 border-black rounded-lg flex items-center justify-center font-bold">
              {language === 'marathi' ? 'फोटो' : 'Photo'}
            </div>
          </div>

          <div className="font-bold text-xl mb-6">
            {language === 'marathi' ? 'सामाजिक कार्याची माहिती :' : 'Social Work Information:'} <span className="text-base font-semibold">{language === 'marathi' ? '(खालीलपैकी ज्या संबंधित कार्य केलेले असेल त्यावर √ अशी खूण करावी.)' : '(Tick √ on the relevant work done from the following)'}</span>
          </div>

          <div className="grid grid-cols-2 gap-y-4 ml-6 font-semibold">
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'बालविवाह प्रतिबंध' : 'Prevention of Child Marriage'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'महिला स्वयं-सहायता बचत गट' : 'Women Self-Help Groups'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'हुंडा निर्मुलन' : 'Eradication of Dowry'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'आरोग्य' : 'Health'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'लिंग चिकित्सा प्रतिबंध' : 'Prevention of Sex Determination'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'साक्षरता' : 'Literacy'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'घरगुती हिंसा प्रतिबंध' : 'Prevention of Domestic Violence'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'मुलींच्या शिक्षणासाठी पुढाकार' : 'Initiative for Girls\' Education'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'महिला सक्षमीकरण' : 'Women Empowerment'}</div>
            <div className="flex items-center"><span className="w-4 flex text-sm">■</span> <span className="border-b border-black border-dashed w-40 inline-block ml-2"></span></div>
          </div>

        </div>

        {/* PAGE 4 - Info & Declaration */}
        <div className="pdf-page bg-white w-[210mm] min-h-[297mm] shadow-xl relative shrink-0 px-20 py-16 text-black text-[17px]">
          <div className="font-bold text-[19px] mb-4 flex justify-center items-center">
            <span className="mr-3 text-sm">■</span> {language === 'marathi' ? 'वरीलपैकी केलेल्या सामाजिक कार्याबद्दल संक्षिप्त माहिती:' : 'Brief information about the social work done above:'}
          </div>

          <div className="border-2 border-black mt-2 mb-10 w-full">
            {[...Array(18)].map((_, i) => (
              <div key={i} className={`h-8 w-full ${i !== 17 ? 'border-b border-black' : ''}`}></div>
            ))}
          </div>

          <div className="font-bold text-xl mb-8 flex justify-center items-center">
            <span className="mr-3 text-sm">■</span> {language === 'marathi' ? 'स्वयं-घोषणापत्र' : 'Self-Declaration'}
          </div>

          <div className="font-medium leading-relaxed text-justify indent-12 mb-6">
            {language === 'marathi' ? 'मी श्रीमती/सौ.' : 'I Smt/Mrs.'} <span className="border-b border-black border-dashed inline-block w-64 mx-2"></span> {language === 'marathi' ? 'रा' : 'Residing at'} <span className="border-b border-black border-dashed inline-block w-40 mx-2"></span>
            {language === 'marathi' ? 'ता.' : 'Taluka'} <span className="border-b border-black border-dashed inline-block w-48 mx-2"></span>{language === 'marathi' ? 'जि.' : 'Dist.'}<span className="border-b border-black border-dashed inline-block w-48 mx-2"></span> {language === 'marathi' ? 'येथील रहिवासी असून मी घोषित करते की,' : 'am a resident of the above and I declare that,'}
          </div>

          <ol className="list-decimal pl-16 space-y-4 font-semibold text-justify mb-16 pr-4">
            <li>{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'} <span className="border-b border-black border-dashed inline-block w-40 mx-2"></span> {language === 'marathi' ? 'येथील पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार- २०२५ साठी मी माझा अर्ज सादर करत आहे.' : 'I am submitting my application for Punyashlok Ahilyadevi Holkar Women\'s Honor Award- 2025.'}</li>
            <li>{language === 'marathi' ? 'मी सामाजिक क्षेत्रात सक्रिय असून सामाजिक व महिला व बालकल्याण क्षेत्रात अर्ज करत असलेल्या ग्रामपंचायत हद्दीत किमान ३ वर्ष काम केलेले आहे.' : 'I am active in the social field and have worked for at least 3 years in the Gram Panchayat limits.'}</li>
            <li>
              {language === 'marathi' ? 'माझ्यावर कोणत्याही प्रकारचा गुन्हा दाखल नाही.' : 'No crime is registered against me.'}<br />
              <span className="font-normal inline-block mt-2 ml-4">
                {language === 'marathi' ? 'वर दिलेली माहिती सत्य असून सदर माहितीत काही चूक आढळल्यास होणा-या कार्यवाहीस मी स्वत: जबाबदार राहील.' : 'The information given above is true and if any mistake is found, I will be personally responsible for the action taken.'}
              </span>
            </li>
          </ol>

          <div className="flex justify-between font-bold mt-16 px-4">
            <div className="space-y-6">
              <div>{language === 'marathi' ? 'दिनांक :' : 'Date:'} &nbsp;&nbsp;&nbsp;&nbsp;/०५/२०२५</div>
              <div>{language === 'marathi' ? 'स्थळ:' : 'Place:'} -------------------</div>
            </div>
            <div className="text-center mt-12 mr-10">
              {language === 'marathi' ? 'अर्जदाराचे नाव व स्वाक्षरी' : 'Applicant\'s Name & Signature'}
            </div>
          </div>

          <div className="absolute bottom-10 text-blue-600 font-medium text-sm hover:underline cursor-pointer ml-4">
            www.majhigrampanchayat.com
          </div>
        </div>

        {/* PAGE 5 - Photos Box */}
        <div className="pdf-page bg-white w-[210mm] min-h-[297mm] shadow-xl relative shrink-0 px-20 py-20 text-black">
          <div className="font-bold text-xl mb-12 flex items-center">
            <span className="mr-3 text-sm">■</span> {language === 'marathi' ? 'सामाजिक कार्याचे फोटोग्राफ्स, बातम्या कात्रणे/ यापूर्वीचे सन्मान इत्यादी' : 'Photographs of social work, news clippings / previous honors etc.'}
            <div className="ml-2 font-medium">{language === 'marathi' ? '(असल्यास)' : '(if any)'}</div>
          </div>

          <div className="space-y-16">
            <div className="w-full h-80 border-[3px] border-[#8dc63f] rounded-tl-[100px] rounded-br-[100px] rounded-tr-xl rounded-bl-xl bg-white"></div>
            <div className="w-full h-80 border-[3px] border-[#8dc63f] rounded-tl-[100px] rounded-br-[100px] rounded-tr-xl rounded-bl-xl bg-white"></div>
          </div>
        </div>

        {/* PAGE 6 - प्राप्त अर्ज व निवड */}
        <div className="pdf-page bg-white w-[210mm] min-h-[297mm] shadow-xl relative shrink-0 px-20 py-20 text-black text-[17px]">
          <div className="flex justify-end mb-8 font-semibold leading-relaxed">
            <div className="w-80">
              <div>{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'} <span className="border-b border-black border-dashed inline-block w-40"></span></div>
              <div>{language === 'marathi' ? 'ता.' : 'Taluka:'}<span className="border-b border-black border-dashed inline-block w-28"></span> {language === 'marathi' ? 'जि.' : 'Dist.:'} <span className="border-b border-black border-dashed inline-block w-28"></span></div>
            </div>
          </div>

          <h3 className="text-center text-xl font-bold mb-4">
            {language === 'marathi' ? 'पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार- २०२५' : 'Punyashlok Ahilyadevi Holkar Women\'s Honor Award- 2025'}
          </h3>
          <h4 className="text-center text-lg font-bold underline underline-offset-4 mb-8">
            {language === 'marathi' ? 'प्राप्त अर्ज, अर्जाची छाननी व पुरस्कारासाठी निवड' : 'Received Applications, Scrutiny and Selection for Award'}
          </h4>

          <p className="font-medium text-justify mb-6">
            {language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Under Gram Panchayat Office'} <span className="border-b border-black border-dashed inline-block w-56"></span> {language === 'marathi' ? 'अंतर्गत पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार- २०२५ साठी खालीलप्रमाणे अर्ज विहीत मुदतीत प्राप्त झाले.' : 'the following applications were received within the prescribed time limit for the Punyashlok Ahilyadevi Holkar Women\'s Honor Award- 2025.'}
          </p>

          <table className="w-full border-collapse border border-black mb-8 text-base">
            <thead>
              <tr className="border border-black">
                <th className="border border-black p-2 font-semibold w-16 text-center">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No'}</th>
                <th className="border border-black p-2 font-semibold w-32 text-center">{language === 'marathi' ? 'दिनांक' : 'Date'}</th>
                <th className="border border-black p-2 font-semibold text-center">{language === 'marathi' ? 'महिलांचे नाव' : 'Women\'s Name'}</th>
                <th className="border border-black p-2 font-semibold w-48 text-center">{language === 'marathi' ? 'गावाचे नाव' : 'Village Name'}</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(10)].map((_, i) => (
                <tr key={i} className="border border-black">
                  <td className="border border-black p-3 text-center">{i+1}.</td>
                  <td className="border border-black p-3"></td>
                  <td className="border border-black p-3"></td>
                  <td className="border border-black p-3"></td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="font-medium text-justify mb-6 leading-relaxed">
            {language === 'marathi' ? 'सदरील प्राप्त अर्जाची छाननी निवड समितीने आज दिनांक' : 'The scrutiny of the received applications was done by the selection committee today on Date'} <span className="border-b border-black border-dashed inline-block w-40"></span> {language === 'marathi' ? 'रोजी केली. छाननीअंती खालील दोन महिलांची निवड “पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार- २०२५” साठी करण्यात आली.' : '. After scrutiny, the following two women were selected for the "Punyashlok Ahilyadevi Holkar Women\'s Honor Award- 2025".'}
          </p>

          <div className="flex justify-between mb-8 px-8 font-semibold">
            <div className="w-1/2 flex items-center">1) <span className="border-b border-black border-dashed inline-block w-64 ml-2 mt-4"></span></div>
            <div className="w-1/2 flex items-center">2) <span className="border-b border-black border-dashed inline-block w-64 ml-2 mt-4"></span></div>
          </div>

          <div className="font-bold flex items-center mb-4">
            <span className="w-4 flex text-sm">■</span> {language === 'marathi' ? 'निवड समिती:' : 'Selection Committee:'}
          </div>

          <table className="w-full border-collapse border border-black text-base">
            <thead>
              <tr className="border border-black">
                <th className="border border-black p-2 font-semibold w-12 text-center">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No'}</th>
                <th className="border border-black p-2 font-semibold text-center w-[40%]">{language === 'marathi' ? 'नाव' : 'Name'}</th>
                <th className="border border-black p-2 font-semibold text-center">{language === 'marathi' ? 'पद' : 'Designation'}</th>
                <th className="border border-black p-2 font-semibold text-center w-32">{language === 'marathi' ? 'स्वाक्षरी' : 'Signature'}</th>
              </tr>
            </thead>
            <tbody>
              {[
                { no: '1', pad: language === 'marathi' ? 'सरपंच (अध्यक्ष)' : 'Sarpanch (President)' },
                { no: '2', pad: language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer' },
                { no: '3', pad: language === 'marathi' ? 'पोलीस पाटील' : 'Police Patil' },
                { no: '4', pad: language === 'marathi' ? 'प्राथमिक शाळेचे मुख्याध्यापक' : 'Primary School Headmaster' },
                { no: '5', pad: language === 'marathi' ? 'अंगणवाडी सेविका' : 'Anganwadi Worker' },
                { no: '6', pad: language === 'marathi' ? 'आशा वर्कर' : 'ASHA Worker' },
              ].map((row, i) => (
                <tr key={i} className="border border-black h-10">
                  <td className="border border-black p-2 text-center">{row.no}.</td>
                  <td className="border border-black p-2"></td>
                  <td className="border border-black p-2">{row.pad}</td>
                  <td className="border border-black p-2"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PAGE 7 - प्रसिद्धी पत्रक */}
        <div className="pdf-page bg-white w-[210mm] min-h-[297mm] shadow-xl relative shrink-0 px-20 py-16 text-black text-[17px]">
          <div className="font-bold text-sm mb-10 text-justify leading-relaxed">
            {language === 'marathi' ? 'वाचा: महिला व बालकल्याण विभाग, महाराष्ट्र शासन, शासन निर्णय दि. ९ मे २०२३ व शासन शुद्धिपत्रक दि. १५ मे २०२३' : 'Read: Women and Child Welfare Dept, Govt of Maharashtra, GR dt May 9, 2023 & Corrigendum dt May 15, 2023'}
          </div>

          <div className="flex justify-end mb-10 font-semibold leading-relaxed">
            <div className="w-80">
              <div>{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'} <span className="border-b border-black border-dashed inline-block w-40"></span></div>
              <div>{language === 'marathi' ? 'ता.' : 'Taluka:'}<span className="border-b border-black border-dashed inline-block w-28"></span> {language === 'marathi' ? 'जि.' : 'Dist.:'} <span className="border-b border-black border-dashed inline-block w-28"></span></div>
              <div>{language === 'marathi' ? 'दिनांक:' : 'Date:'} ----/०५/२०२५</div>
            </div>
          </div>

          <h3 className="text-center text-2xl font-bold underline underline-offset-8 mb-10 decoration-2">
            {language === 'marathi' ? 'प्रसिद्धी पत्रक' : 'Press Release'}
          </h3>

          <p className="text-justify leading-relaxed font-medium mb-8 indent-16">
            {language === 'marathi' 
              ? <>या प्रसिद्धीपत्रकाद्वारे ग्रामपंचायत <span className="border-b border-black border-dashed inline-block w-56"></span> येथील सर्व महिला व ग्रामस्थांना कळविण्यात येते की, संदर्भीय शासन निर्णयानुसार पुण्यश्लोक अहिल्यादेवी होळकर यांच्या जयंती निमित्त दि. ३१ मे २०२५ रोजी ग्रामपंचायत/गट ग्रामपंचायत क्षेत्रातील सामाजिक तसेच महिला व बालकल्याण क्षेत्रात उत्कृष्ट कार्य करीत असलेल्या दोन कर्तबगार महिलांना <span className="font-bold">“पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार”</span> ग्रामपंचायतीमार्फत देण्यात येणार आहे.</>
              : <>Through this press release, all women and villagers of Gram Panchayat <span className="border-b border-black border-dashed inline-block w-56"></span> are informed that according to the reference government resolution, on the occasion of the birth anniversary of Punyashlok Ahilyadevi Holkar on May 31, 2025, the <span className="font-bold">"Punyashlok Ahilyadevi Holkar Women's Honor Award"</span> will be given by the Gram Panchayat to two capable women working excellently in the social and women and child welfare fields.</>}
          </p>

          <p className="text-justify leading-relaxed font-medium mb-6">
            {language === 'marathi' ? 'आपल्या ग्रामपंचायत' : 'Under our Gram Panchayat'} <span className="border-b border-black border-dashed inline-block w-64 mx-2"></span> {language === 'marathi' ? 'अंतर्गत “पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार- २०२५” साठी खालील दोन महिलांची निवड, निवड समितीद्वारे करण्यात आलेली आहे:' : ', the following two women have been selected by the selection committee for the "Punyashlok Ahilyadevi Holkar Women\'s Honor Award- 2025":'}
          </p>

          <table className="w-full border-collapse border border-black mb-10 text-base">
            <thead>
              <tr className="border border-black">
                <th className="border border-black p-3 font-semibold w-16 text-center">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No'}</th>
                <th className="border border-black p-3 font-semibold text-center">{language === 'marathi' ? 'महिलांचे नाव' : 'Women\'s Name'}</th>
                <th className="border border-black p-3 font-semibold w-64 text-center">{language === 'marathi' ? 'गावाचे नाव' : 'Village Name'}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border border-black h-12">
                <td className="border border-black p-4 text-center">1.</td>
                <td className="border border-black p-4"></td>
                <td className="border border-black p-4"></td>
              </tr>
              <tr className="border border-black h-12">
                <td className="border border-black p-4 text-center">2.</td>
                <td className="border border-black p-4"></td>
                <td className="border border-black p-4"></td>
              </tr>
            </tbody>
          </table>

          <p className="text-justify leading-relaxed font-medium mb-8 indent-12">
            {language === 'marathi' ? 'सदरील पुरस्काराचे वितरण पुण्यश्लोक अहिल्यादेवी होळकर यांच्या जयंतीदिनी दिनांक ३१/०५/२०२५ रोजी सकाळी/दुपारी' : 'The said award will be distributed on the birth anniversary of Punyashlok Ahilyadevi Holkar on 31/05/2025 at morning/afternoon'} <span className="border-b border-black border-dashed inline-block w-32"></span> {language === 'marathi' ? 'वाजता' : 'time at'} <span className="border-b border-black border-dashed inline-block w-64"></span> {language === 'marathi' ? 'या ठिकाणी ग्रामपंचायतीचे सरपंच/प्रशासक/गावातील प्रतिष्ठित व्यक्ती यांच्या हस्ते करण्यात येईल.' : 'venue by the Sarpanch/Administrator/Eminent person of the Gram Panchayat.'}
          </p>

          <p className="text-justify font-medium mb-20 indent-12">
            {language === 'marathi' ? 'करिता कार्यक्रमासाठी सर्वांनी उपस्थित राहावे, ही विनंती.' : 'Therefore, it is requested that everyone should attend the program.'}
          </p>

          <div className="flex justify-end font-bold mr-12 mt-10">
            <div className="text-center">
              <div className="mb-8">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</div>
              <div>{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'} -------------------------</div>
            </div>
          </div>
        </div>

        {/* PAGE 8 - निवड पत्र */}
        <div className="pdf-page bg-white w-[210mm] min-h-[297mm] shadow-xl relative shrink-0 px-20 py-20 text-black text-[17px]">
          <div className="flex justify-end mb-12 font-semibold leading-relaxed">
            <div className="w-80">
              <div>{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'} <span className="border-b border-black border-dashed inline-block w-40"></span></div>
              <div>{language === 'marathi' ? 'ता.' : 'Taluka:'}<span className="border-b border-black border-dashed inline-block w-28"></span> {language === 'marathi' ? 'जि.' : 'Dist.:'} <span className="border-b border-black border-dashed inline-block w-28"></span></div>
              <div>{language === 'marathi' ? 'दिनांक:' : 'Date:'} ----/०५/२०२५</div>
            </div>
          </div>

          <div className="font-semibold leading-relaxed mb-10">
            <div>{language === 'marathi' ? 'प्रति,' : 'To,'}</div>
            <div className="mt-4 flex items-center">
              {language === 'marathi' ? 'श्रीमती/सौ.' : 'Smt/Mrs.'} <span className="border-b border-black border-dashed inline-block w-[300px] ml-2 mt-2"></span>
            </div>
            <div className="mt-4 flex items-center">
              {language === 'marathi' ? 'राहणार' : 'Residing at'} <span className="border-b border-black border-dashed inline-block w-[300px] ml-2 mt-2"></span>
            </div>
          </div>

          <div className="font-bold text-center leading-relaxed max-w-[85%] mx-auto mb-10 text-[18px]">
            <div>{language === 'marathi' ? 'विषय: “पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार- २०२५” साठी आपली निवड झाल्याबाबत.' : 'Subject: Regarding your selection for the "Punyashlok Ahilyadevi Holkar Women\'s Honor Award- 2025".'}</div>
            <div className="text-left mt-6 ml-6 space-y-4">
              <div>{language === 'marathi' ? 'संदर्भ: 1) महिला व बालकल्याण विभाग, महाराष्ट्र शासन, शासन निर्णय दि. ९ मे २०२३ व' : 'Ref: 1) Women and Child Welfare Dept, Govt of Maharashtra, GR dt May 9, 2023 &'}</div>
              <div className="pl-14">{language === 'marathi' ? 'शासन शुद्धिपत्रक दि. १५ मे २०२३' : 'Corrigendum dt May 15, 2023'}</div>
              <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{language === 'marathi' ? '2) आपला सदरील पुरस्कारासाठीचा अर्ज दि.' : '2) Your application for the said award dt.'}</div>
            </div>
          </div>

          <div className="font-bold mb-6">
            {language === 'marathi' ? 'महोदया,' : 'Madam,'}
          </div>

          <p className="text-justify leading-relaxed font-medium mb-10 indent-16">
            {language === 'marathi' ? 'आपणांस कळविण्यात आनंद वाटतो की, ग्रामपंचायत' : 'We are pleased to inform you that you have been selected for the "Punyashlok Ahilyadevi Holkar Women\'s Honor Award- 2025" given by Gram Panchayat'} <span className="border-b border-black border-dashed inline-block w-48"></span> {language === 'marathi' ? 'कडून देण्यात येणारा “पुण्यश्लोक अहिल्यादेवी होळकर महिला सन्मान पुरस्कार- २०२५” साठी आपली निवड झालेली आहे.' : '.'}
          </p>

          <div className="font-bold text-[17px] mb-6">
            {language === 'marathi' ? 'पुरस्कार वितरण कार्यक्रम:' : 'Award Distribution Program:'}
          </div>

          <div className="space-y-6 font-medium ml-4 mb-10 leading-relaxed">
            <div>
              {language === 'marathi' ? 'दिनांक ३१/०५/२०२५ रोजी, सकाळी/दुपारी' : 'On Date 31/05/2025, morning/afternoon'} <span className="border-b border-black border-dashed inline-block w-32"></span> {language === 'marathi' ? 'वाजता' : 'time'}
            </div>
            <div className="flex">
              {language === 'marathi' ? 'कार्यक्रमाचे ठिकाण:' : 'Program Venue:'} <span className="flex-1 border-b border-black border-dashed mx-2 mt-[2px]"></span> {language === 'marathi' ? 'या ठिकाणी' : ''}
            </div>
            <div>{language === 'marathi' ? 'आयोजित केलेला आहे.' : 'is organized.'}</div>
          </div>

          <p className="font-medium indent-12 mt-8 mb-24">
            {language === 'marathi' ? 'करिता आपण उपस्थित राहून पुरस्काराचा स्वीकार करावा, ही विनंती!' : 'Therefore, it is requested that you attend and accept the award!'}
          </p>

          <div className="flex justify-end font-bold mr-12 mt-12">
            <div className="text-center">
              <div className="mb-8">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</div>
              <div>{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'} -------------------------</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AhilyadeviForm;
